import React from "react";
import { Dialog, DialogTitle, DialogContent, Alert, Divider } from "@mui/material";
import { styled } from "@mui/system";
import MDBox from "../../../../../components/MDBox";
import MDTypography from "../../../../../components/MDTypography";
import MDButton from "../../../../../components/MDButton";
import SegmentThresholds from "./SegmentThresholds";
import ProcessedDataPreview from "../ProcessedDataPreview";
import { Segment, SegmentationMethod } from "../../types/types";
import { useSegments } from "../../hooks/useSegments";
import { useHeadroomContext } from "../../context/HeadRoomProvider";

export interface ColumnRange {
  isNumeric: boolean;
  min?: number;
  max?: number;
}

interface ManualSegmentationModalProps {
  open: boolean;
  onClose: () => void;
  onCreateSegments: (segments: Segment[], method: SegmentationMethod) => void;
  segmentIndex: number;
  initialSegments: Segment[];
  columnRanges: Record<string, ColumnRange>;
  type: string;
}

const ManualSegmentationModal: React.FC<ManualSegmentationModalProps> = ({
  open,
  onClose,
  onCreateSegments,
  segmentIndex,
  initialSegments,
  columnRanges,
  type,
}) => {
  const { setShouldSkipSegmentationGroupedCountQuery } = useHeadroomContext();
  const column = type && columnRanges[type];
  const isNumeric = column?.isNumeric;
  const min = column?.min;
  const max = column?.max;

  if (open && (!type || !isNumeric || typeof min !== "number" || typeof max !== "number")) {
    onClose();
    return null;
  }

  const {
    segments,
    numberOfSegments,
    handleMinValueChange,
    handleMaxValueChange,
    handleAddSegment,
    handleRemoveSegment,
    handleRemoveLastSegment,
  } = useSegments({
    index: segmentIndex,
    initialSegments,
    initialNumberOfSegments: initialSegments.length || 2,
    columnRanges,
    type,
    onSegmentChange: () => setShouldSkipSegmentationGroupedCountQuery(true),
  });

  const rangeErrors: string[] = React.useMemo(() => {
    if (!isNumeric || !type) return [];
    if (min === undefined || max === undefined) return [];
    return segments
      ?.map((segment, index) => {
        if (segment.minValue < min || segment.maxValue > max) {
          return `Segment ${index + 1} range (${segment.minValue} - ${
            segment.maxValue
          }) is outside column range (${min} - ${max})`;
        }
        return null;
      })
      .filter((error): error is string => error !== null);
  }, [segments, isNumeric, min, max, type]);

  const handleCreateSegments = () => {
    // if (rangeErrors.length > 0) return;
    onCreateSegments(segments, "MANUAL");
    setShouldSkipSegmentationGroupedCountQuery(false);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleCreateSegments();
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose} onKeyDown={handleKeyDown} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ padding: "12px 16px" }}>
        <MDTypography variant="h5" fontWeight="medium" sx={{ textAlign: "center", textTransform: "uppercase" }}>
          Manual Segmentation - Segment {segmentIndex + 1}
        </MDTypography>
      </DialogTitle>
      <StyledDialogContent>
        {/* {rangeErrors.length > 0 && (
          <MDBox mb={1}>
            {rangeErrors.map((error, index) => (
              <Alert key={index} severity="error" sx={{ fontSize: "0.85rem", padding: "8px" }}>
                {error}
              </Alert>
            ))}
          </MDBox>
        )} */}
        {!isNumeric && type && (
          <MDBox mb={1}>
            <Alert severity="error" sx={{ fontSize: "0.85rem", padding: "8px" }}>
              Please select a numeric column for manual segmentation
            </Alert>
          </MDBox>
        )}
        <SegmentThresholds
          segments={segments}
          numberOfSegments={numberOfSegments}
          handleMinValueChange={handleMinValueChange}
          handleMaxValueChange={handleMaxValueChange}
          handleAddSegment={handleAddSegment}
          handleRemoveSegment={handleRemoveSegment}
          handleRemoveLastSegment={handleRemoveLastSegment}
          columnRanges={columnRanges}
          type={type}
        />
        <MDBox mt={2} mb={2} display="flex" justifyContent="flex-end" gap={1}>
          <MDButton
            variant="outlined"
            onClick={onClose}
            sx={{ borderRadius: "6px", padding: "4px 8px", fontSize: "0.7rem" }}
          >
            Cancel
          </MDButton>
          <MDButton
            variant="background"
            onClick={handleCreateSegments}
            disabled={/*rangeErrors.length > 0 ||*/ !isNumeric}
            sx={{ borderRadius: "6px", padding: "4px 8px", fontSize: "0.7rem" }}
          >
            Create Segments
          </MDButton>
        </MDBox>
        <Divider sx={{ my: 0.5 }} />
        <ProcessedDataPreview tableWidth="100%" />
      </StyledDialogContent>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    width: "80%",
    maxWidth: "900px",
    borderRadius: "8px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export default ManualSegmentationModal;
